// ── Static data constants ─────────────────────────────────────────────────────

export const VID = {
  bigbuck:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
  fun: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
  meltdowns:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4",
  joyrides:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
  escapes:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
  elephant:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
  sintel:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4",
  tears:
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4",
};

const face = (s) =>
  `https://images.unsplash.com/${s}?auto=format&fit=facearea&facepad=2.6&w=400&h=400&q=80`;

export const PROGRAMS = [
  {
    id: "leadership-fundamentals",
    title: "Leadership Fundamentals",
    blurb:
      "Master the core principles of effective leadership and team management with AI-powered coaching.",
    guru: {
      name: "Amina Okafor",
      role: "Executive Coach · ex-McKinsey",
      avatar: face("photo-1573496359142-b8d87734a5a2"),
      bio: "Amina spent 8 years at McKinsey building high-performing teams across EMEA before pivoting to executive coaching. She works with senior leaders and founders on the human side of leadership — the conversations that either compound or cost you.\n\nHer coaching philosophy is simple: most leadership problems are communication problems in disguise. Whether it's a team that won't push back, a peer relationship that's quietly broken, or a direct report stuck at the same ceiling — Amina helps you find the exact intervention that shifts things.\n\nShe holds an ICF Professional Coach Certification, a postgraduate in Leadership Psychology from UCL, and has coached over 500 executives across financial services, tech and media.",
      credentials: [
        "ICF Certified Coach",
        "ex-McKinsey Associate",
        "Leadership Psychology, UCL",
        "500+ executives coached",
      ],
    },
    category: "Leadership",
    level: "Intermediate",
    duration: "6 weeks",
    lessons: 24,
    sims: 14,
    rating: 4.8,
    reviews: 122,
    learners: "1,200+",
    outcome: "92% of learners improved confidence in 2 weeks",
    tags: ["Communication", "Decision Making", "Team Building"],
    accent: ["#01ECFF", "#962EFF"],
    video: VID.bigbuck,
    poster:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Coaching conversations",
        desc: "Hold 1:1s that build trust and unlock performance.",
        sessions: [
          {
            type: "skill",
            title: "The art of the open question",
            freeTrial: true,
          },
          {
            type: "skill",
            title: "Building psychological safety",
            freeTrial: true,
          },
          {
            type: "sim",
            title: "Coach a disengaged direct report",
            freeTrial: true,
          },
        ],
      },
      {
        name: "Delegation",
        desc: "Match the task to the person, not the title.",
        sessions: [
          { type: "skill", title: "Matching tasks to strengths" },
          { type: "skill", title: "Setting clear expectations" },
          { type: "sim", title: "Delegate a high-stakes project" },
        ],
      },
      {
        name: "Hard feedback",
        desc: "Say the thing — kindly, clearly, in the moment.",
        sessions: [
          { type: "skill", title: "The SBI feedback model" },
          { type: "skill", title: "Timing and environment" },
          { type: "sim", title: "Deliver difficult feedback to a senior peer" },
        ],
      },
      {
        name: "Strategic thinking",
        desc: "Zoom out, set direction, communicate the why.",
        sessions: [
          { type: "skill", title: "Separating signal from noise" },
          { type: "skill", title: "Communicating strategy to your team" },
          { type: "sim", title: "Present a strategic pivot to leadership" },
        ],
      },
      {
        name: "Team rituals",
        desc: "Standups, retros, 1:1s — the cadence that compounds.",
        sessions: [
          { type: "skill", title: "Designing high-signal standups" },
          { type: "skill", title: "Running retrospectives that stick" },
          { type: "sim", title: "Facilitate a team retro gone sideways" },
        ],
      },
      {
        name: "Conflict navigation",
        desc: "Turn disagreement into shared progress.",
        sessions: [
          { type: "skill", title: "Identifying conflict types" },
          { type: "skill", title: "De-escalation moves" },
          {
            type: "sim",
            title: "Navigate a heated disagreement between two reports",
          },
        ],
      },
    ],
    featured: true,
  },
  {
    id: "project-management",
    title: "Project Management Essentials",
    blurb:
      "Run real projects with confidence — scope, schedule, ship. Practise tough trade-offs in simulation.",
    guru: {
      name: "Graham Mendel",
      role: "PMP · 15+ years shipping",
      avatar: face("photo-1507003211169-0a1dd7228f2d"),
      bio: "Graham has shipped projects at Apple, Stripe and three startups. With 15+ years in the field, he brings the hard-won lessons of real delivery to every session — no filler, just frameworks that work.",
      credentials: [
        "PMP Certified",
        "ex-Apple & Stripe",
        "Agile & Waterfall practitioner",
        "200+ projects delivered",
      ],
    },
    category: "Operations",
    level: "Beginner",
    duration: "5 weeks",
    lessons: 20,
    sims: 10,
    rating: 4.7,
    reviews: 864,
    learners: "900+",
    outcome: "87% delivered their next project on time",
    tags: ["Planning", "Stakeholders", "Risk"],
    accent: ["#FFB85C", "#962EFF"],
    video: VID.fun,
    poster:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Scoping",
        desc: "Cut the project to the smallest valuable shape.",
        sessions: [
          { type: "skill", title: "Defining scope and success criteria" },
          { type: "skill", title: "Saying no without losing trust" },
          {
            type: "sim",
            title: "Scope a new product feature with a demanding client",
          },
        ],
      },
      {
        name: "Stakeholder maps",
        desc: "Know who needs what, when, and why.",
        sessions: [
          { type: "skill", title: "Mapping influence and interest" },
          { type: "skill", title: "Communicating up, down, and sideways" },
          {
            type: "sim",
            title: "Manage conflicting priorities from two executives",
          },
        ],
      },
      {
        name: "Risk registers",
        desc: "Name the dragons before they bite.",
        sessions: [
          { type: "skill", title: "Identifying and categorising risks" },
          { type: "skill", title: "Building mitigation plans" },
          { type: "sim", title: "Respond to an unplanned blocker mid-sprint" },
        ],
      },
      {
        name: "Status updates",
        desc: "Crisp, honest, no-surprises comms.",
        sessions: [
          { type: "skill", title: "The one-page status report" },
          { type: "skill", title: "Delivering bad news early" },
          {
            type: "sim",
            title: "Present a slipping deadline to a senior stakeholder",
          },
        ],
      },
    ],
  },
  {
    id: "effective-communication",
    title: "Effective Communication",
    blurb:
      "Land the message every time — meetings, presentations, hard conversations. Live practice with Rika.",
    guru: {
      name: "Rika Tanaka",
      role: "Speech coach · TEDx alum",
      avatar: face("photo-1544005313-94ddf0286df2"),
      bio: "A TEDx speaker and speech coach, Rika has helped over 2,000 professionals find their voice. Her frameworks are used by executives at Google, Salesforce and beyond — because great communication is a skill, not a gift.",
      credentials: [
        "TEDx Speaker",
        "Certified Speech Coach",
        "Berkeley Rhetoric alum",
        "2,000+ professionals coached",
      ],
    },
    category: "Communication",
    level: "Beginner",
    duration: "4 weeks",
    lessons: 18,
    sims: 12,
    rating: 4.9,
    reviews: 2104,
    learners: "2,000+",
    outcome: "94% felt more confident presenting to senior leaders",
    tags: ["Presenting", "Listening", "Storytelling"],
    accent: ["#01ECFF", "#5C8CFF"],
    video: VID.joyrides,
    poster:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Story arcs",
        desc: "The shape of a talk people actually remember.",
        sessions: [
          { type: "skill", title: "The three-act structure for presentations" },
          { type: "skill", title: "Opening hooks that hold attention" },
          { type: "sim", title: "Pitch a complex idea in under 3 minutes" },
        ],
      },
      {
        name: "Active listening",
        desc: "Hear what's said — and what isn't.",
        sessions: [
          { type: "skill", title: "Reflective listening techniques" },
          { type: "skill", title: "Reading between the lines" },
          {
            type: "sim",
            title: "Navigate a conversation where the real issue stays hidden",
          },
        ],
      },
      {
        name: "Status & vocal range",
        desc: "Use voice and posture as a tool, not a tell.",
        sessions: [
          { type: "skill", title: "Projecting presence without volume" },
          { type: "skill", title: "Pacing and pause as emphasis" },
          { type: "sim", title: "Present under pressure to a sceptical panel" },
        ],
      },
      {
        name: "Slide hygiene",
        desc: "Less deck, more point.",
        sessions: [
          { type: "skill", title: "One idea per slide" },
          { type: "skill", title: "Data visualisation that earns trust" },
          {
            type: "sim",
            title: "Defend your deck against a detail-obsessed exec",
          },
        ],
      },
    ],
  },
  {
    id: "difficult-conversations",
    title: "Difficult Conversations",
    blurb:
      "Performance issues, layoffs, missed expectations — rehearse the calls that matter most.",
    guru: {
      name: "Marcus Bell",
      role: "HR Director · 20 years",
      avatar: face("photo-1500648767791-00dcc994a43e"),
      bio: "Marcus has navigated HR challenges at some of the world's largest employers for over 20 years. He specialises in the human moments that define careers — and teaches you to handle them without flinching.",
      credentials: [
        "CIPD Fellow",
        "HR Director (FTSE 100)",
        "Conflict Mediation Certified",
        "20+ years in HR leadership",
      ],
    },
    category: "Leadership",
    level: "Advanced",
    duration: "3 weeks",
    lessons: 12,
    sims: 16,
    rating: 4.9,
    reviews: 1581,
    learners: "1,500+",
    outcome: "91% reported clearer outcomes from tough talks",
    tags: ["Feedback", "Empathy", "Boundaries"],
    accent: ["#FF6B9D", "#962EFF"],
    video: VID.meltdowns,
    poster:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Opening lines",
        desc: "How you start sets the tone for everything.",
        sessions: [
          {
            type: "skill",
            title: "Setting intent without triggering defensiveness",
          },
          { type: "skill", title: "The first 30 seconds framework" },
          {
            type: "sim",
            title: "Open a conversation about repeated underperformance",
          },
        ],
      },
      {
        name: "Naming behaviour",
        desc: "Be specific. Stay on the action, not the person.",
        sessions: [
          { type: "skill", title: "Observable vs. inferential language" },
          {
            type: "skill",
            title: "Giving examples without examples becoming the argument",
          },
          {
            type: "sim",
            title: "Address a pattern of behaviour with a defensive colleague",
          },
        ],
      },
      {
        name: "Holding the room",
        desc: "When emotion arrives — and it will — stay grounded.",
        sessions: [
          { type: "skill", title: "Regulating your own nervous system" },
          { type: "skill", title: "Responding, not reacting" },
          {
            type: "sim",
            title: "Stay composed when the conversation turns confrontational",
          },
        ],
      },
    ],
  },
  {
    id: "consultative-sales",
    title: "Consultative Sales",
    blurb:
      "Sell by understanding. Practise discovery, objections, and closing with realistic AI buyers.",
    guru: {
      name: "Daniela Cruz",
      role: "VP Sales · SaaS specialist",
      avatar: face("photo-1580489944761-15a19d654956"),
      bio: "Daniela led revenue teams to $100M+ ARR at three SaaS companies. She's obsessive about the quality of conversations — not just the close rate — and that obsession is baked into every module.",
      credentials: [
        "VP Sales (3× SaaS scale-ups)",
        "$100M+ ARR led",
        "Sandler & SPIN certified",
        "Top 1% LinkedIn Sales Insider",
      ],
    },
    category: "Sales",
    level: "Intermediate",
    duration: "8 weeks",
    lessons: 32,
    sims: 24,
    rating: 4.8,
    reviews: 3201,
    learners: "3,200+",
    outcome: "Avg. close rate +18% after 30 days",
    tags: ["Discovery", "Objections", "Closing"],
    accent: ["#01ECFF", "#17B26A"],
    video: VID.escapes,
    poster:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Discovery questions",
        desc: "Find the real pain — not the one in the brief.",
        sessions: [
          { type: "skill", title: "The SPIN framework" },
          { type: "skill", title: "Silence as a discovery tool" },
          {
            type: "sim",
            title: "Uncover hidden blockers with a guarded buyer",
          },
        ],
      },
      {
        name: "Objection handling",
        desc: "Turn 'too expensive' into a budget conversation.",
        sessions: [
          { type: "skill", title: "Classifying objections by type" },
          { type: "skill", title: "Reframing price as value" },
          {
            type: "sim",
            title: "Handle a multi-objection call without losing momentum",
          },
        ],
      },
      {
        name: "Closing language",
        desc: "Ask for the business, then shut up.",
        sessions: [
          { type: "skill", title: "Assumptive vs. direct closing styles" },
          { type: "skill", title: "Reading buying signals" },
          {
            type: "sim",
            title: "Close a deal where the prospect keeps deferring",
          },
        ],
      },
    ],
  },
  {
    id: "confidence-at-work",
    title: "Confidence at Work",
    blurb:
      "Quiet the inner critic, speak up in meetings, ask for what you're worth. Built for new managers.",
    guru: {
      name: "Jordan Reyes",
      role: "Career coach · Author",
      avatar: face("photo-1438761681033-6461ffad8d80"),
      bio: "Jordan is a career coach and author whose book 'Ask For It' became a bestseller. She works with early-career professionals and first-time managers to build the kind of authentic confidence that compounds over time.",
      credentials: [
        "Author, 'Ask For It'",
        "ICF Certified Coach",
        "Forbes 30 Under 30",
        "10,000+ coaching hours",
      ],
    },
    category: "Personal Growth",
    level: "Beginner",
    duration: "3 weeks",
    lessons: 14,
    sims: 8,
    rating: 4.9,
    reviews: 1820,
    learners: "1,800+",
    outcome: "89% asked for what they wanted within 2 weeks",
    tags: ["Confidence", "Negotiation", "Mindset"],
    accent: ["#962EFF", "#FF6B9D"],
    video: VID.tears,
    poster:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Inner critic",
        desc: "Notice it, name it, get on with the work.",
        sessions: [
          { type: "skill", title: "Understanding your inner narrative" },
          { type: "skill", title: "Separating fact from feeling" },
          {
            type: "sim",
            title: "Speak up in a meeting where you feel out of your depth",
          },
        ],
      },
      {
        name: "Speaking up",
        desc: "Say the thing in the meeting, not in the slack DM after.",
        sessions: [
          { type: "skill", title: "Framing contributions that land" },
          { type: "skill", title: "Recovering from being talked over" },
          {
            type: "sim",
            title: "Challenge a senior opinion in front of the team",
          },
        ],
      },
      {
        name: "Asking for the raise",
        desc: "Frame, time, and deliver the ask.",
        sessions: [
          { type: "skill", title: "Building the case with evidence" },
          { type: "skill", title: "Timing and context" },
          {
            type: "sim",
            title: "Negotiate a salary increase with a resistant manager",
          },
        ],
      },
    ],
  },
  {
    id: "negotiation",
    title: "Negotiation Mastery",
    blurb:
      "Trade with confidence — vendors, clients, your own boss. Live AI counter-parties push back hard.",
    guru: {
      name: "Priya Shah",
      role: "Ex-trial lawyer · Mediator",
      avatar: face("photo-1487412720507-e7ab37603c6f"),
      bio: "A former trial lawyer turned mediator, Priya has facilitated negotiations worth over $1B. She brings legal rigour and human empathy to every interaction — and teaches you to do the same.",
      credentials: [
        "Called to the Bar (Gray's Inn)",
        "Certified Mediator, CEDR",
        "$1B+ in negotiations facilitated",
        "Harvard Negotiation Programme",
      ],
    },
    category: "Sales",
    level: "Advanced",
    duration: "5 weeks",
    lessons: 20,
    sims: 20,
    rating: 4.7,
    reviews: 942,
    learners: "950+",
    outcome: "Avg. settlement +22% better than baseline",
    tags: ["BATNA", "Anchoring", "Mediation"],
    accent: ["#FFB85C", "#01ECFF"],
    video: VID.elephant,
    poster:
      "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "BATNA",
        desc: "Walk in knowing your floor — and theirs.",
        sessions: [
          { type: "skill", title: "Calculating your walk-away point" },
          { type: "skill", title: "Researching the other side's alternatives" },
          {
            type: "sim",
            title: "Negotiate a contract when you hold less leverage",
          },
        ],
      },
      {
        name: "Anchoring",
        desc: "First number sets the centre of gravity.",
        sessions: [
          { type: "skill", title: "Setting the anchor without being obvious" },
          { type: "skill", title: "Countering an extreme opening offer" },
          {
            type: "sim",
            title: "Open a vendor negotiation under time pressure",
          },
        ],
      },
      {
        name: "Mediation moves",
        desc: "Defuse, reframe, find the deal hiding in plain sight.",
        sessions: [
          { type: "skill", title: "Interest-based vs. positional bargaining" },
          { type: "skill", title: "Reframing to expand the pie" },
          { type: "sim", title: "Mediate between two parties at an impasse" },
        ],
      },
    ],
  },
  {
    id: "sales",
    title: "Sales",
    blurb:
      "Build a repeatable sales process from first touch to close. Practise every stage with realistic AI buyers.",
    guru: {
      name: "Ryan Carter",
      role: "Head of Sales · B2B specialist",
      avatar: face("photo-1560250097-0b93528c311a"),
      bio: "Ryan has led sales teams across SaaS, fintech and professional services for 15 years, consistently hitting 120%+ of quota. He built the sales playbook that took one startup from zero to £30M ARR and now teaches the frameworks he wishes he'd had on day one.\n\nHis approach is practical and direct: no fluff, no gimmicks — just the conversations, habits and mindset shifts that separate top performers from the rest.",
      credentials: [
        "15+ years in B2B sales",
        "£30M ARR playbook author",
        "Sandler Selling Certified",
        "500+ reps trained",
      ],
    },
    category: "Sales",
    level: "Beginner",
    duration: "6 weeks",
    lessons: 24,
    sims: 18,
    rating: 4.8,
    reviews: 1540,
    learners: "1,500+",
    outcome: "Avg. pipeline conversion +21% after 30 days",
    tags: ["Prospecting", "Discovery", "Closing"],
    accent: ["#17B26A", "#01ECFF"],
    video: VID.escapes,
    poster:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Prospecting",
        desc: "Fill the top of the funnel with the right names.",
        sessions: [
          { type: "skill", title: "Building an ideal customer profile" },
          { type: "skill", title: "Cold outreach that gets replies" },
          { type: "sim", title: "Break through a gatekeeper to reach the decision maker", freeTrial: true },
        ],
      },
      {
        name: "Discovery",
        desc: "Ask better questions, uncover real pain.",
        sessions: [
          { type: "skill", title: "The SPIN framework in practice" },
          { type: "skill", title: "Listening for what isn't being said" },
          { type: "sim", title: "Run a discovery call with a guarded prospect" },
        ],
      },
      {
        name: "Objection handling",
        desc: "Turn resistance into momentum.",
        sessions: [
          { type: "skill", title: "Classifying objections by root cause" },
          { type: "skill", title: "Reframing price as ROI" },
          { type: "sim", title: "Handle back-to-back objections without losing the room" },
        ],
      },
      {
        name: "Closing",
        desc: "Ask for the business — then stop talking.",
        sessions: [
          { type: "skill", title: "Reading buying signals" },
          { type: "skill", title: "Trial close vs. direct close" },
          { type: "sim", title: "Close a deal where the prospect keeps deferring" },
        ],
      },
    ],
    featured: true,
  },
  {
    id: "leadership-development",
    title: "Leadership Development",
    blurb:
      "Grow from manager to leader. Build the skills that create followership, trust, and high-performing teams.",
    guru: {
      name: "Serena Blake",
      role: "Leadership Coach · ex-Deloitte",
      avatar: face("photo-1573497019940-1c28c88b4f3e"),
      bio: "Serena spent a decade developing leaders at Deloitte before founding her own coaching practice. She's worked with C-suite executives and first-time managers alike, helping them make the leap from doing the work to leading the people who do it.\n\nHer programmes focus on the real challenges of leadership — navigating ambiguity, building trust at scale, and staying grounded under pressure — using frameworks she's refined across hundreds of coaching engagements.",
      credentials: [
        "ICF Master Certified Coach",
        "ex-Deloitte Leadership Development",
        "Organisational Psychology, LSE",
        "300+ leaders coached",
      ],
    },
    category: "Leadership",
    level: "Intermediate",
    duration: "8 weeks",
    lessons: 30,
    sims: 20,
    rating: 4.9,
    reviews: 1876,
    learners: "1,800+",
    outcome: "88% reported stronger team trust within 4 weeks",
    tags: ["Influence", "Vision", "Team Development"],
    accent: ["#962EFF", "#01ECFF"],
    video: VID.bigbuck,
    poster:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Leadership identity",
        desc: "Know your style — and when to flex it.",
        sessions: [
          { type: "skill", title: "Situational leadership models" },
          { type: "skill", title: "Adapting your style to the person" },
          { type: "sim", title: "Lead a team member who needs a different approach than yours", freeTrial: true },
        ],
      },
      {
        name: "Building trust",
        desc: "The compound interest of consistent behaviour.",
        sessions: [
          { type: "skill", title: "The trust equation" },
          { type: "skill", title: "Reliability and follow-through" },
          { type: "sim", title: "Rebuild trust after a decision that landed badly" },
        ],
      },
      {
        name: "Developing others",
        desc: "Your job is to make yourself replaceable.",
        sessions: [
          { type: "skill", title: "Stretching people without breaking them" },
          { type: "skill", title: "Giving feedback that drives growth" },
          { type: "sim", title: "Have a growth conversation with a high-potential team member" },
        ],
      },
      {
        name: "Leading through change",
        desc: "Bring your team with you — even when the path isn't clear.",
        sessions: [
          { type: "skill", title: "Communicating uncertainty with confidence" },
          { type: "skill", title: "Holding the vision when morale dips" },
          { type: "sim", title: "Rally your team through an unexpected restructure" },
        ],
      },
    ],
  },
  {
    id: "presenting-powerfully",
    title: "Presenting Powerfully",
    blurb:
      "Command any room — boardrooms, all-hands, client pitches. Practise live with an AI audience that challenges you.",
    guru: {
      name: "Marcus Webb",
      role: "Executive Presence Coach · TEDx",
      avatar: face("photo-1519085360753-af0119f7cbe7"),
      bio: "Marcus has coached over 1,500 executives to present with clarity, conviction and presence. A TEDx speaker himself, he's worked with leaders at FTSE 100 companies and fast-growth startups to transform the way they show up on stage, in the boardroom and on camera.\n\nHis approach combines rhetorical craft with real-world simulation — because knowing what to say is only half the battle.",
      credentials: [
        "TEDx Speaker",
        "Executive Presence Coach",
        "Rhetoric, Oxford",
        "1,500+ executives coached",
      ],
    },
    category: "Communication",
    level: "Intermediate",
    duration: "5 weeks",
    lessons: 20,
    sims: 16,
    rating: 4.9,
    reviews: 2340,
    learners: "2,300+",
    outcome: "93% felt more confident presenting to the board",
    tags: ["Storytelling", "Presence", "Slides"],
    accent: ["#FFB85C", "#FF6B9D"],
    video: VID.joyrides,
    poster:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Structure & narrative",
        desc: "Give your presentation a spine people can follow.",
        sessions: [
          { type: "skill", title: "The pyramid principle for presentations" },
          { type: "skill", title: "Opening hooks that silence the room" },
          { type: "sim", title: "Present a complex strategy to a sceptical board", freeTrial: true },
        ],
      },
      {
        name: "Executive presence",
        desc: "What you project is as important as what you say.",
        sessions: [
          { type: "skill", title: "Voice, pace and deliberate pause" },
          { type: "skill", title: "Managing nerves under pressure" },
          { type: "sim", title: "Hold the room when the audience gets hostile" },
        ],
      },
      {
        name: "Slide design",
        desc: "Less deck, more point.",
        sessions: [
          { type: "skill", title: "One message per slide" },
          { type: "skill", title: "Data visuals that earn trust" },
          { type: "sim", title: "Defend your deck against a detail-obsessed exec" },
        ],
      },
      {
        name: "Q&A mastery",
        desc: "The part everyone fears — handled with confidence.",
        sessions: [
          { type: "skill", title: "Bridging tough questions back to your message" },
          { type: "skill", title: "Handling 'I don't know' with authority" },
          { type: "sim", title: "Navigate a Q&A that turns into an ambush" },
        ],
      },
    ],
  },
  {
    id: "coaching-skills",
    title: "Coaching Skills",
    blurb:
      "Coach your team to find their own answers. Build the conversations that unlock performance and build trust.",
    guru: {
      name: "Nina Osei",
      role: "Master Coach · ICF",
      avatar: face("photo-1551836022-deb4988cc6c0"),
      bio: "Nina is an ICF Master Certified Coach with 12 years of experience developing coaching capability in organisations across finance, tech and the public sector. She believes that the single highest-leverage skill a manager can build is the ability to coach — and that most managers have never been taught how.\n\nHer programmes are practical, repeatable and grounded in real conversation — designed to be used from day one.",
      credentials: [
        "ICF Master Certified Coach",
        "12+ years coaching in organisations",
        "Applied Psychology, UCL",
        "400+ managers trained",
      ],
    },
    category: "Leadership",
    level: "Beginner",
    duration: "4 weeks",
    lessons: 16,
    sims: 14,
    rating: 4.8,
    reviews: 1120,
    learners: "1,100+",
    outcome: "90% held better 1:1s in the first week",
    tags: ["Listening", "Questions", "1:1s"],
    accent: ["#01ECFF", "#17B26A"],
    video: VID.elephant,
    poster:
      "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "Coaching mindset",
        desc: "Stop solving. Start asking.",
        sessions: [
          { type: "skill", title: "Manager vs. coach — knowing the difference" },
          { type: "skill", title: "Shifting from advice to inquiry" },
          { type: "sim", title: "Resist the urge to fix it and coach instead", freeTrial: true },
        ],
      },
      {
        name: "Powerful questions",
        desc: "The right question does more than ten answers.",
        sessions: [
          { type: "skill", title: "Open vs. closed questions in practice" },
          { type: "skill", title: "The question behind the question" },
          { type: "sim", title: "Coach a stuck team member using only questions" },
        ],
      },
      {
        name: "The GROW model",
        desc: "A framework for every coaching conversation.",
        sessions: [
          { type: "skill", title: "Goal — Reality — Options — Will" },
          { type: "skill", title: "Staying in the model when conversation drifts" },
          { type: "sim", title: "Run a GROW session with a disengaged direct report" },
        ],
      },
      {
        name: "Accountability",
        desc: "Close the loop and make commitments stick.",
        sessions: [
          { type: "skill", title: "Contracting for action, not intent" },
          { type: "skill", title: "Following up without micromanaging" },
          { type: "sim", title: "Hold someone accountable who repeatedly misses commitments" },
        ],
      },
    ],
  },
  {
    id: "customer-service-excellence",
    title: "Customer Service Excellence",
    blurb:
      "Turn frustrated customers into loyal ones. Practise high-stakes calls without burning a real account.",
    guru: {
      name: "Lena Müller",
      role: "Head of CX · Hospitality",
      avatar: face("photo-1534528741775-53994a69daeb"),
      bio: "Lena built the CX teams at two 5-star hotel groups and a global airline before turning to coaching. She believes exceptional service is a mindset, not a script — and teaches you to own both.",
      credentials: [
        "Hospitality CX Specialist",
        "NPS improvement lead (airline + hotel)",
        "Certified Customer Experience Professional",
        "15+ years in luxury service",
      ],
    },
    category: "Service",
    level: "Beginner",
    duration: "4 weeks",
    lessons: 16,
    sims: 18,
    rating: 4.6,
    reviews: 1340,
    learners: "1,300+",
    outcome: "NPS +14 within first month of practice",
    tags: ["De-escalation", "Empathy", "Recovery"],
    accent: ["#17B26A", "#01ECFF"],
    video: VID.sintel,
    poster:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=900&q=80",
    topics: [
      {
        name: "De-escalation",
        desc: "Slow it down. Lower the volume. Hold the room.",
        sessions: [
          { type: "skill", title: "Tone, pace, and presence under pressure" },
          { type: "skill", title: "Acknowledging without agreeing" },
          {
            type: "sim",
            title: "Handle an angry customer threatening to escalate",
          },
        ],
      },
      {
        name: "Service recovery",
        desc: "Turn the failure into the loyalty moment.",
        sessions: [
          { type: "skill", title: "The HEARD recovery framework" },
          { type: "skill", title: "When to compensate and when not to" },
          {
            type: "sim",
            title: "Recover a relationship after a serious service failure",
          },
        ],
      },
      {
        name: "Boundary setting",
        desc: "Be kind. Be clear. Stay employed.",
        sessions: [
          { type: "skill", title: "Saying no with warmth" },
          { type: "skill", title: "Documenting unreasonable requests" },
          {
            type: "sim",
            title: "Decline an unreasonable demand without losing the customer",
          },
        ],
      },
    ],
  },
];

export const QUICK_CHIPS = [
  "All",
  "Leadership",
  "Communication",
  "Sales",
  "Service",
  "Operations",
  "Personal Growth",
];

export const REVIEWS = [
  {
    name: "Sarah Johnson",
    initials: "SJ",
    rating: 5,
    timeAgo: "2 weeks ago",
    verified: true,
    completed: true,
    text: "Amina is an amazing instructor! The instant Guru-led feedback really helped me understand complex concepts and where I need to improve. The learning path allowed me to take things at my own pace and re-visit topics easily.",
  },
  {
    name: "Mike Chen",
    initials: "MC",
    rating: 4,
    timeAgo: "1 month ago",
    verified: true,
    completed: true,
    text: "This program exceeded my expectations. The interactive Q&A feature is a game-changer! Being able to ask questions anytime and get instant, detailed responses made all the difference.",
  },
  {
    name: "Emily Rodriguez",
    initials: "ER",
    rating: 5,
    timeAgo: "1 month ago",
    verified: true,
    completed: false,
    text: "Great content and structure. Amina explains everything clearly and the pace is perfect. Still working through it but loving the experience so far!",
  },
  {
    name: "James Kumar",
    initials: "JK",
    rating: 5,
    timeAgo: "1 month ago",
    verified: true,
    completed: true,
    text: "The simulation challenges are unlike anything I've done before. Having a real conversation with an AI that pushes back like a genuine colleague made me realise where my blind spots were. Highly recommend.",
  },
  {
    name: "Aisha Patel",
    initials: "AP",
    rating: 5,
    timeAgo: "2 months ago",
    verified: true,
    completed: true,
    text: "I came in sceptical but left a convert. The coaching conversations module alone changed how I run my 1:1s. My team has noticed the difference.",
  },
  {
    name: "Tom Whitfield",
    initials: "TW",
    rating: 4,
    timeAgo: "2 months ago",
    verified: true,
    completed: false,
    text: "Really practical framework for giving feedback. The SBI model is something I now use every week. Would love more advanced simulations in later modules.",
  },
];
